import React, { useMemo, useCallback, useEffect, useState } from "react";
import { getTextWidth } from "@utils";
import { WIDTHS, ACTION_COLUMN_WIDTH } from "../HyroTable.consts";
import { MenuItem } from "@mui/material";
import HyroThreeDotMenu from "@hyro-components/HyroThreeDotMenu";
import ColumnHeader from "../components/ColumnHeader";
import TableCell from "../components/TableCell";
import { gridClasses } from "@mui/x-data-grid";

export default function useTableData({
  columns = [],
  rows = [],
  rowActions,
  onRowUpdate,
  currentEditableField,
  testId,
  columnStyle,
  overrideMaxRowHeight,
  truncateCellText,
  clearSelection,
  apiRef,
  selectedColumn
}) {
  const [isActionsSticky, setIsActionsSticky] = useState(false);
  const normalizedRows = useMemo(() => rows.map((row, index) => ({ index, ...row })), [rows]);
  const selectedColumnIndex = useMemo(() => columns.findIndex(column => column.key === selectedColumn), [columns, selectedColumn]);

  useEffect(() => {
    if (selectedColumn && selectedColumnIndex !== -1 && apiRef.current) {
      const columnElement = apiRef.current.getColumnHeaderElement(columns[selectedColumnIndex].field);
      if (columnElement) {
        columnElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center"
        });
      }
    }
  }, [selectedColumn, selectedColumnIndex, apiRef, columns]);

  useEffect(() => {
    if (!apiRef?.current) return;
    const handleScrollPositionChange = (params) => {
      const gridElement = apiRef.current?.rootElementRef?.current?.querySelector(`.${gridClasses.virtualScroller}`);
      if (gridElement) {
        const maxScrollLeft = gridElement.scrollWidth - gridElement.clientWidth;
        const left = Math.ceil(params.left);
        setIsActionsSticky(left > 0 && left < maxScrollLeft);
      }
    };
    const unsubscribeScrollPositionChange = apiRef.current.subscribeEvent("scrollPositionChange", handleScrollPositionChange);
    return () => {
      unsubscribeScrollPositionChange();
    };
  }, [apiRef]);

  useEffect(() => {
    normalizedRows.forEach((row, idx) => {
      if (row.id === undefined || row.id === null || row.id === "") {
        console.error("HyroTable: Row missing id", row, "at index", idx);
      }
    });
  }, [normalizedRows]);

  const handleRowUpdate = useCallback((newRow, oldRow) => {
    const hasCellChanged = currentEditableField && newRow[currentEditableField] !== oldRow?.[currentEditableField];
    const cellNewValue = newRow[currentEditableField];

    if (hasCellChanged) {
      const updatedRow = onRowUpdate?.({ fieldName: currentEditableField, newValue: cellNewValue, newRow });
      return updatedRow || newRow;
    }

    return newRow;
  }, [currentEditableField, onRowUpdate]);

  const getActionsColumn = useCallback(() => [
    {
      field: "actions",
      headerName: "",
      width: ACTION_COLUMN_WIDTH,
      sortable: false,
      renderCell: ({ row, api }) => {
        const computeRowActions = Array.isArray(rowActions) ? rowActions : rowActions?.(row);
        if (!computeRowActions) return null;

        return (
          <HyroThreeDotMenu
            testId={`${testId}-table-row-${row.index}`}
            onMenuOpen={() => {
              clearSelection?.();
            }}
            menuItems={computeRowActions?.map(({ display, id, run }) => (
              <MenuItem
                key={id}
                data-testid={`${testId}-three-dot-menu-${display.replace(" ", "-")}`}
                onClick={() => run(row)}
              >
                {display}
              </MenuItem>
            ))}
          />
        );
      }
    }
  ], [rowActions, testId, clearSelection]);

  const normalizeColumnsWidth = useCallback((columns) => columns?.map(({ field, headerName, width: overrideWidth, flex, asArray }, index) => {
    const longestColumnContent = rows.map(row => {
      if (Array.isArray(row[field])) return row[field][0];
      return row[field];
    }).reduce((acc, curr) => {
      let width = getTextWidth({ text: curr, font: "600 14px Inter, sans-serif" });

      if (typeof curr === "object" && curr !== null) {
        width = Object.values(curr).reduce((acc, curr) => {
          const currWidth = getTextWidth({ text: curr, font: "600 14px Inter, sans-serif", extraWidth: WIDTHS.EXTRA_PADDING });
          return currWidth > acc ? currWidth : acc;
        }, 0);
      }

      if (!Array.isArray(curr) && width > acc) return width;
      return acc;
    }, "");

    const isFieldAnArray = rows.some(row => row[field] && Array.isArray(row[field]));
    const columnWidth = getTextWidth({
      text: headerName,
      extraWidth: WIDTHS.EXTRA_PADDING,
      font: "600 14px Inter, sans-serif"
    });

    const actualWidth = columnWidth > longestColumnContent ? columnWidth : longestColumnContent;
    let finalWidth;

    if (isFieldAnArray || asArray) {
      finalWidth = actualWidth + WIDTHS.ARRAY_EXTRA_WIDTH;
    } else {
      finalWidth = actualWidth + (index === 0 ? WIDTHS.EXTRA_PADDING_FIRST_COLUMN : WIDTHS.EXTRA_PADDING);
    }
    if (overrideWidth) finalWidth = overrideWidth;
    if (finalWidth > WIDTHS.COLUMN_MAX_WIDTH) finalWidth = WIDTHS.COLUMN_MAX_WIDTH;

    return {
      ...columns[index],
      minWidth: finalWidth,
      flex: flex ? flex : overrideWidth ? 0 : 1
    };
  }), [rows]);

  const addDefaultRenderCell = useCallback((columns) => columns.map((column, colIndex) => ({
    ...column,
    renderHeader: (params) => (
      <ColumnHeader
        params={params}
        column={column}
        colIndex={colIndex}
        testId={testId}
        columnStyle={columnStyle}
        overrideMaxRowHeight={overrideMaxRowHeight}
      />
    ),
    renderCell: (params) => (
      <TableCell
        params={params}
        column={column}
        colIndex={colIndex}
        truncateCellText={truncateCellText}
        overrideMaxRowHeight={overrideMaxRowHeight}
      />
    ),
    renderEditCell: (params) => (
      <>
        {column.renderEditCell(params)}
      </>
    )
  })), [testId, columnStyle, overrideMaxRowHeight, truncateCellText]);

  const normalizedCols = useMemo(() => {
    const hasRowActions = Array.isArray(rowActions) || rows.some(row => rowActions?.(row));
    const cols = normalizeColumnsWidth(columns);
    if (hasRowActions) {
      cols.push(...getActionsColumn());
    }
    return addDefaultRenderCell(cols);
  }, [columns, rows, rowActions, getActionsColumn, addDefaultRenderCell, normalizeColumnsWidth]);

  return {
    normalizedRows,
    handleRowUpdate,
    normalizedCols,
    isActionsSticky,
    selectedColumnIndex
  };
}
